import { useState, useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { Link2, Copy, Check, ExternalLink, Share2, Globe, Sparkles } from 'lucide-react';
import { buildSubdomainUrl } from '../../utils/subdomain';

export default function SubdomainDashboard() {
  const { user } = useAuthContext();
  const [copied, setCopied] = useState(false);
  const [subdomainUrl, setSubdomainUrl] = useState('');

  useEffect(() => {
    if (user?.username) {
      setSubdomainUrl(buildSubdomainUrl(user.username));
    }
  }, [user?.username]);

  const handleCopy = async () => {
    if (subdomainUrl) {
      await navigator.clipboard.writeText(subdomainUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (navigator.share && subdomainUrl) {
      try {
        await navigator.share({
          title: `${user?.displayName || user?.username} - EduMi`,
          text: 'Check out my courses on EduMi!',
          url: subdomainUrl
        });
      } catch (err) {
        console.log('Share cancelled or failed');
      }
    }
  };

  if (!user || user.plan !== 'yearly' || !user.username) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-blue-200">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-blue-600 to-pink-600 rounded-xl">
          <Globe className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            Pro+ Custom Domain
            <Sparkles className="w-5 h-5 text-blue-600" />
          </h3>
          <p className="text-gray-600">Your exclusive branded URL</p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-blue-100 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Custom Domain
        </label>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-gray-50 rounded-lg px-4 py-3 font-mono text-lg text-blue-700 border-2 border-blue-200">
            {user.username}.edumi.co
          </div>
          <button
            onClick={handleCopy}
            className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
          >
            {copied ? (
              <>
                <Check className="w-5 h-5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-5 h-5" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a
          href={subdomainUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 rounded-lg border-2 border-gray-200 transition-colors font-medium"
        >
          <ExternalLink className="w-5 h-5" />
          Visit Site
        </a>

        {navigator.share && (
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 rounded-lg border-2 border-gray-200 transition-colors font-medium"
          >
            <Share2 className="w-5 h-5" />
            Share
          </button>
        )}

        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(subdomainUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 rounded-lg border-2 border-gray-200 transition-colors font-medium"
        >
          <Share2 className="w-5 h-5" />
          Share on Facebook
        </a>
      </div>

      <div className="mt-6 p-4 bg-blue-100 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Link2 className="w-4 h-4" />
          How it works
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Your custom domain is automatically active</li>
          <li>• Share this URL to promote your courses</li>
          <li>• Professional branded presence for your students</li>
          <li>• No setup required - works instantly!</li>
        </ul>
      </div>
    </div>
  );
}
